import { Component, Input, ViewChild, ElementRef, AfterViewInit, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import intlTelInput from 'intl-tel-input';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

@Component({
    selector: 'agpr-phone-number-input',
    templateUrl: './phone-number-input.component.html',
    styleUrls: ['./phone-number-input.component.scss'],
    providers: [getProvider(PhoneNumberInputComponent)],
    standalone: false
})
/**
 * This component uses the intl-tel-input library
 * https://github.com/jackocnr/intl-tel-input
 */
export class PhoneNumberInputComponent extends ControlValueAccessorModel<string> implements AfterViewInit, OnDestroy {
    @Input() public id: string;
    @Input() public label: string;
    @Input() public placeholder: string;
    @Input() public tooltip: string;
    @Input() public initialCountry: string;
    @Input() public required: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public initialFocus: boolean = false;
    @Input() public bottomMargin: boolean = true;
    @Input() public readOnly: boolean = false;
    @Input() public placeHoldCode: boolean = false;
    @Input() public allowExtension: boolean = true;
    @Input() public autoPlaceholder: boolean;
    @Input() public maxLength: number;

    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() keyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() isValidNumber: EventEmitter<{valid: boolean; error: string}> = new EventEmitter<{valid: boolean; error: string}>();

    @ViewChild('input') inputView: ElementRef;

    public input: HTMLInputElement;
    public intlTelInput;

    // copied this from i18n.phonenumbers.PhoneNumberUtil.ValidationResult
    // in https://github.com/googlei18n/libphonenumber/blob/master/javascript/i18n/phonenumbers/phonenumberutil.js
    // Modified and added custom errors
    /* eslint-disable @typescript-eslint/naming-convention */
    readonly validationErrorCode = {
        0: 'VALID',
        1: 'INVALID_COUNTRY_CODE',
        2: 'TOO_SHORT',
        3: 'TOO_LONG',
        4: 'IS_POSSIBLE_LOCAL_ONLY',
        5: 'INVALID_LENGTH',
        6: 'INVALID_FORMAT',
        [-99]: 'BAD'
    };
    // If the error messages must be translated, the values can be changed with localization tokens
    readonly validationError = {
        VALID: '',
        INVALID_COUNTRY_CODE: 'INVALID_COUNTRY_CODE',
        TOO_SHORT: 'TOO_SHORT',
        TOO_LONG: 'TOO_LONG',
        IS_POSSIBLE_LOCAL_ONLY: 'IS_POSSIBLE_LOCAL_ONLY',
        INVALID_LENGTH: 'INVALID_LENGTH',
        INVALID_FORMAT: 'INVALID_FORMAT',
        BAD: 'BAD'
    };

    constructor(public localizationService: LocalizationService){
        super();
    }

    ngAfterViewInit(): void {
        if (this.initialFocus) {
            this.giveFocus();
        }
        this.input = <HTMLInputElement>this.inputView.nativeElement;

        const options = {
            autoPlaceholder: this.autoPlaceholder ? 'polite' : 'off',
            customPlaceholder: (!this.placeholder) ? undefined : () => this.placeholder,
            initialCountry: this.initialCountry,
            preferredCountries: [],
        };
        this.intlTelInput = intlTelInput(this.input, options);
        this.resetDefaultStyle();
        if (this.placeHoldCode) {
            // @HACK to avoid getting 'Expression has changed after it was checked. Previous value: 'null'. Current value: '+1'.'
            setTimeout(() => {
                this.value = '+' + this.intlTelInput.getSelectedCountryData().dialCode + ' ';
            }, 0);
        }
    }

    giveFocus() {
        if (this.input) {
            requestAnimationFrame(() => this.inputView.nativeElement.focus());
        }
    }

    onKeydown(event: KeyboardEvent) {
        this.keydown.emit(event);
    }

    onKeyUp(event: KeyboardEvent) {
        this.keyup.emit(event);
    }

    computeChanges() {
        this.validNumber();
        // by default the extension number is ignore, I'm modifying it here to add the extension numbers too
        let extension = '';
        if (this.allowExtension && this.intlTelInput.getExtension()) {
            extension = 'x' + this.intlTelInput.getExtension();
        }
        this.onModelChange(this.intlTelInput.getNumber() + extension);
    }

    onBlur(event: FocusEvent): void {
        this.blur.emit(event);
    }

    resetDefaultStyle(){
        this.input.parentElement.style.width = '100%';
        this.input.parentElement.style.display = 'block';
    }

    validNumber(){
        let error = this.validationError[this.validationErrorCode[this.intlTelInput.getValidationError()]];
        const valid = this.intlTelInput.isValidNumber();
        if (!valid && error === ''){
            // there can be a case where the number is not valid but also there isn't any validation errors
            // This happen in cases that the error is very specific, for example adding characters to the number for a country that does not support characters
            error = this.validationError.INVALID_FORMAT;
        }

        this.isValidNumber.emit({
            valid,
            error
        });
    }

    ngOnDestroy(){
        this.intlTelInput.destroy();
    }
}
